import styled from 'styled-components';

export const Card = styled.div`
  background: ${props => props.theme.colors.bg};
  border-radius: ${props => props.borderRadius || '12px'};
  padding: ${props => props.padding || '20px'};
  box-shadow: ${props => 
    props.elevated ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 
    '0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 24px rgba(0, 0, 0, 0.06)'
  };
  margin-bottom: ${props => props.marginBottom || '0'};
  border: ${props => 
    props.border ? `1px solid ${props.theme.colors.border}` : 
    'none'
  };
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  ${props => props.hoverable && `
    cursor: pointer;
    
    &:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  ${props => props.clickable && `
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: ${props.theme.colors.bg2};
    }
    
    &:active {
      transform: scale(0.98);
    }
  `}
  
  ${props => props.fullWidth && `
    width: 100%;
  `}
  
  @media (max-width: 768px) {
    padding: ${props => props.mobilePadding || props.padding || '16px'};
    border-radius: ${props => props.mobileBorderRadius || props.borderRadius || '8px'};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: ${props => props.alignItems || 'center'};
  margin-bottom: ${props => props.marginBottom || '16px'};
  padding-bottom: ${props => props.withDivider ? '12px' : '0'};
  border-bottom: ${props => 
    props.withDivider ? `1px solid ${props.theme.colors.border}` : 'none'
  };
  
  ${props => props.stackOnMobile && `
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
  `}
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: ${props => 
    props.size === 'large' ? '24px' : 
    props.size === 'small' ? '16px' : 
    '18px'
  };
  font-weight: ${props => props.weight || '600'};
  color: ${props => props.theme.colors.text};
  line-height: 1.3;
  
  ${props => props.truncate && `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const CardSubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.4;
  
  ${props => props.truncate && `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const CardContent = styled.div`
  line-height: 1.6;
  color: ${props => props.theme.colors.text};
  
  ${props => props.scrollable && `
    max-height: ${props.maxHeight || '300px'};
    overflow-y: auto;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${props.theme.colors.bg2};
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${props.theme.colors.border};
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: ${props.theme.colors.textSecondary};
    }
  `}
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: ${props => props.justify || 'flex-end'};
  align-items: center;
  gap: ${props => props.gap || '12px'};
  margin-top: ${props => props.marginTop || '16px'};
  padding-top: ${props => props.withDivider ? '12px' : '0'};
  border-top: ${props => 
    props.withDivider ? `1px solid ${props.theme.colors.border}` : 'none'
  };
  
  ${props => props.stackOnMobile && `
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      
      button, a {
        width: 100%;
        justify-content: center;
      }
    }
  `}
`;

export const CardActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

// Specialized card variants
export const StatsCard = styled(Card)`
  text-align: center;
  padding: 24px 20px;
`;

export const StatsValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.color || props.theme.colors.primary};
  margin-bottom: 8px;
`;

export const StatsLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ImageCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

export const ImageCardMedia = styled.div`
  width: 100%;
  height: ${props => props.height || '200px'};
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  background-color: ${props => props.theme.colors.bg2};
`;

export const ImageCardContent = styled.div`
  padding: ${props => props.padding || '20px'};
`;
